import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  DateField,
} from '@structural-climbing/ui'
import { useTrainers } from './useTrainers'
import type { TrainerAssignment } from './useTrainerAssignments'

interface Props {
  assignment: TrainerAssignment
  onClose: () => void
  onReassigned: () => void
}

/**
 * Hands a member to a different trainer mid-block — the other half of the
 * vacation story, for when the cover is permanent rather than a few days.
 *
 * The current trainer is excluded from the list: reassigning to the person who
 * already has them is the one thing this cannot do, and the server refuses it
 * anyway.
 */
export function ReassignTrainerModal({ assignment, onClose, onReassigned }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { trainers, loading } = useTrainers()

  const [trainerId, setTrainerId] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Nor can a member become their own trainer. Their child can, which is a
  // parent who coaches their kids.
  const options = trainers.filter(
    (tr) =>
      tr.user_id !== assignment.trainer_user_id &&
      !(tr.user_id === assignment.member_user_id && !assignment.child_id),
  )

  async function handleSubmit() {
    if (!trainerId) return
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(`${apiUrl}/trainer-assignments/${assignment.id}/reassign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ trainer_user_id: trainerId, starts_at: startsAt || null }),
      })
      if (!resp.ok) {
        const { error: msg } = (await resp.json()) as { error?: string }
        throw new Error(msg ?? t('trainers.errorReassign'))
      }
      onReassigned()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('trainers.errorReassign'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal scrollBehavior="inside" isOpen onOpenChange={(open) => !open && onClose()}>
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex-col items-start gap-1">
              <span>{t('trainers.reassignTitle')}</span>
              <span className="text-sm font-normal text-foreground/60">
                {assignment.child_name ?? assignment.trainer_name}
              </span>
            </ModalHeader>
            <ModalBody>
              {loading ? (
                <div className="flex justify-center py-6">
                  <Spinner size="sm" color="primary" />
                </div>
              ) : options.length === 0 ? (
                <p className="py-4 text-sm text-foreground/60">{t('trainers.noOtherTrainers')}</p>
              ) : (
                <div className="flex flex-col gap-4">
                  <Select
                    label={t('trainers.newTrainerLabel')}
                    selectedKeys={trainerId ? [trainerId] : []}
                    onSelectionChange={(keys) =>
                      setTrainerId((Array.from(keys)[0] as string) ?? '')
                    }
                  >
                    {options.map((tr) => (
                      <SelectItem key={tr.user_id} textValue={tr.name}>
                        {tr.name}
                      </SelectItem>
                    ))}
                  </Select>
                  <DateField
                    label={t('trainers.takeoverLabel')}
                    value={startsAt}
                    onValueChange={setStartsAt}
                    description={t('trainers.takeoverHint')}
                  />
                  <p className="text-xs text-foreground/50">
                    {assignment.ends_at
                      ? t('trainers.reassignKeepsEnd', { date: assignment.ends_at })
                      : t('trainers.reassignOpenEnded')}
                  </p>
                  {error && <p className="text-sm text-danger">{error}</p>}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onClick={onClose} isDisabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit}
                isLoading={saving}
                isDisabled={!trainerId || options.length === 0}
              >
                {t('trainers.reassignAction')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
